/**
 * NiftyTrader API Module
 * Tier 1 fallback for live options data
 *
 * API: https://webapi.niftytrader.in/webapi/Option/option-chain-calculator-data
 * Provides: Pre-calculated OI changes, Greeks, buildup signals
 */

import { type IndexSymbol } from '../config/constants'

export const NIFTYTRADER_CONFIG = {
    BASE_URL: 'https://webapi.niftytrader.in/webapi/Option/option-chain-calculator-data',
    SYMBOLS: {
        NIFTY: {
            symbol: 'nifty',
            exchange: 'nse',
            displayName: 'NIFTY',
        },
        BANKNIFTY: {
            symbol: 'banknifty',
            exchange: 'nse',
            displayName: 'BANKNIFTY',
        },
        SENSEX: {
            symbol: 'sensex',
            exchange: 'bse',
            displayName: 'SENSEX',
        },
    } as Record<string, { symbol: string; exchange: string; displayName: string }>,
    getApiUrl: (symbol: string, expiry: string, time?: string): string => {
        const config = NIFTYTRADER_CONFIG.SYMBOLS[symbol]
        if (!config) throw new Error(`Unsupported symbol for NiftyTrader: ${symbol}`)

        let url = `${NIFTYTRADER_CONFIG.BASE_URL}?symbol=${config.symbol}&expiryDate=${expiry}&exchange=${config.exchange}`
        if (time) {
            url += `&createdTime=${time}`
        }
        return url
    },
}

// ============================================================================
// TYPES
// ============================================================================

export interface NiftyTraderStrike {
    strike_price: number
    calls_oi: number
    calls_change_oi: number
    calls_volume: number
    calls_ltp: number
    calls_bid_price: number
    calls_ask_price: number
    calls_iv: number
    calls_delta: number
    calls_gamma: number
    calls_theta: number
    calls_vega: number
    calls_buildup: string
    puts_oi: number
    puts_change_oi: number
    puts_volume: number
    puts_ltp: number
    puts_bid_price: number
    puts_ask_price: number
    puts_iv: number
    puts_delta: number
    puts_gamma: number
    puts_theta: number
    puts_vega: number
    puts_buildup: string
    pcr: number
    index_close: number
}

export interface NiftyTraderResponse {
    optiondata: NiftyTraderStrike[]
    index_close: number
    atm_strike: number
    expiry_date: string
    symbol: string
    time: string
}

// ============================================================================
// TRANSFORM FUNCTIONS
// ============================================================================

/**
 * Transform NiftyTrader strike to our format
 */
export function transformNiftyTraderStrike(strike: NiftyTraderStrike, timestamp: string): any {
    return {
        strike: strike.strike_price,
        strikePrice: strike.strike_price,

        // Call data
        callOpenInterest: strike.calls_oi || 0,
        callOpenInterestChange: strike.calls_change_oi || 0,
        callVolume: strike.calls_volume || 0,
        callPrice: strike.calls_ltp || 0,
        callLTP: strike.calls_ltp || 0,
        callIV: strike.calls_iv || 0,
        callDelta: strike.calls_delta || 0,
        callGamma: strike.calls_gamma || 0,
        callTheta: strike.calls_theta || 0,
        callVega: strike.calls_vega || 0,
        callBuildup: strike.calls_buildup || '',
        callBid: strike.calls_bid_price || 0,
        callAsk: strike.calls_ask_price || 0,

        // Put data
        putOpenInterest: strike.puts_oi || 0,
        putOpenInterestChange: strike.puts_change_oi || 0,
        putVolume: strike.puts_volume || 0,
        putPrice: strike.puts_ltp || 0,
        putLTP: strike.puts_ltp || 0,
        putIV: strike.puts_iv || 0,
        putDelta: strike.puts_delta || 0,
        putGamma: strike.puts_gamma || 0,
        putTheta: strike.puts_theta || 0,
        putVega: strike.puts_vega || 0,
        putBuildup: strike.puts_buildup || '',
        putBid: strike.puts_bid_price || 0,
        putAsk: strike.puts_ask_price || 0,

        // Common
        pcr: strike.pcr || 0,
        underlyingValue: strike.index_close || 0,
        timestamp,
    }
}

/**
 * Transform full NiftyTrader response to our format
 */
export function transformNiftyTraderResponse(data: NiftyTraderResponse): any {
    // Convert HH:MM:SS to full timestamp (assuming Today)
    let timestamp = Date.now()
    if (data.time) {
        try {
            const now = new Date()
            const parts = data.time.split(':')
            if (parts.length >= 2) {
                now.setHours(parseInt(parts[0] || '0'), parseInt(parts[1] || '0'), parseInt(parts[2] || '0'), 0)
                timestamp = now.getTime()
            }
        } catch (e) { console.warn('Ignored error in niftytrader-api.ts:', e); }
    }

    const strikes = (data.optiondata || []).map(
        (strike) => transformNiftyTraderStrike(strike, data.time), // Keep string here if needed for display, or update to ISO?
        // transformNiftyTraderStrike uses 'timestamp' param as is.
        // Let's pass the ISO string or keep original time string?
        // The transformer typically ignores strike-level timestamp if snapshot has one.
    )

    return {
        success: true,
        source: 'niftytrader',
        timestamp: timestamp, // Number
        displayTime: data.time, // Keep original string for display if needed
        underlyingValue: data.index_close,
        atmStrike: data.atm_strike,
        expiry: data.expiry_date,
        symbol: data.symbol?.toUpperCase(),
        optionChain: strikes,
        chain: strikes,
    }
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * Get NiftyTrader symbol config
 */
export function getNiftyTraderSymbolConfig(symbol: IndexSymbol | string) {
    const key = symbol.toString().toUpperCase() as IndexSymbol
    return NIFTYTRADER_CONFIG.SYMBOLS[key] || null
}

/**
 * Format expiry date for NiftyTrader API (YYYY-MM-DD)
 */
export function formatExpiryForNiftyTrader(expiry: string): string {
    // Input: "DD-MM-YYYY" or "YYYY-MM-DD"
    if (!expiry) return ''

    // Check if already in YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(expiry)) {
        return expiry
    }

    // Convert DD-MM-YYYY to YYYY-MM-DD
    const parts = expiry.split('-')
    if (parts.length === 3) {
        // Determine if DD-MM-YYYY or MM-DD-YYYY
        const first = parseInt(parts[0] || '0', 10)
        if (first > 12) {
            // DD-MM-YYYY format
            return `${parts[2]}-${parts[1]}-${parts[0]}`
        }
    }

    return expiry
}

/**
 * Format time for NiftyTrader API (HH:MM:SS)
 */
export function formatTimeForNiftyTrader(time: string): string {
    // Input: "HH:MM" or "HH:MM:SS"
    if (!time) return ''

    const parts = time.split(':')
    if (parts.length === 2) {
        return `${parts[0]}:${parts[1]}:00`
    }

    return time
}

/**
 * Get current time formatted for NiftyTrader API (HH:MM:00)
 * Clamps to market hours: 9:15 AM to 3:30 PM IST
 * - Before market open: returns 09:16:00
 * - After market close: returns 15:29:00 (last trading minute)
 * - During market hours: returns current time
 */
export function getCurrentTimeForNiftyTrader(): string {
    const now = new Date()
    const hours = now.getHours()
    const minutes = now.getMinutes()

    // Market hours: 9:15 AM to 3:30 PM IST
    const currentTotalMinutes = hours * 60 + minutes
    const marketOpenMinutes = 9 * 60 + 15 // 9:15 AM = 555 minutes
    const marketCloseMinutes = 15 * 60 + 30 // 3:30 PM = 930 minutes

    // Before market open - use market open time
    if (currentTotalMinutes < marketOpenMinutes) {
        // console.log('[NiftyTrader] Before market open, using 09:16:00')
        return '09:16:00'
    }

    // After market close - use last trading minute (15:29)
    if (currentTotalMinutes >= marketCloseMinutes) {
        // console.log('[NiftyTrader] After market close, using 15:29:00')
        return '15:29:00'
    }

    // During market hours - use current time
    const hoursStr = hours.toString().padStart(2, '0')
    const minutesStr = minutes.toString().padStart(2, '0')
    return `${hoursStr}:${minutesStr}:00`
}

/**
 * Fetch current minute data from NiftyTrader API
 * This is the PRIMARY source for live data (Tier 1)
 * Uses server-side proxy to bypass CORS restrictions
 */
export async function fetchNiftyTraderMinute(
    symbol: IndexSymbol | string,
    expiry: string,
    time?: string, // Optional: for fetching specific historical minute
): Promise<any> {
    const config = getNiftyTraderSymbolConfig(symbol)
    if (!config) {
        console.error(`[NiftyTrader] Unsupported symbol: ${symbol}`)
        return null
    }

    try {
        const formattedExpiry = formatExpiryForNiftyTrader(expiry)

        // createdTime is REQUIRED by NiftyTrader API
        // Use provided time or current time (always with :00 seconds)
        const createdTime = time ? formatTimeForNiftyTrader(time) : getCurrentTimeForNiftyTrader()

        // Build proxy URL manually to avoid URL-encoding colons in createdTime
        // NiftyTrader API expects literal colons, not %3A
        const proxyUrl = `${window.location.origin}/api/niftytrader-proxy?symbol=${config.symbol}&expiryDate=${formattedExpiry}&exchange=${config.exchange}&createdTime=${createdTime}`

        // console.log(`[NiftyTrader] Fetching via proxy: ${proxyUrl}`)

        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 20000) // 20s timeout

        const response = await fetch(proxyUrl, {
            signal: controller.signal,
            headers: {
                Accept: 'application/json',
            },
        })

        clearTimeout(timeoutId)

        if (!response.ok) {
            console.warn(`[NiftyTrader] Proxy Error: ${response.status}`)
            return null
        }

        const data = (await response.json()) as NiftyTraderResponse

        // Check for proxy error
        if ((data as any).error) {
            console.warn(`[NiftyTrader] API Error: ${(data as any).error}`)
            return null
        }

        if (!data.optiondata || data.optiondata.length === 0) {
            console.warn('[NiftyTrader] No option data in response')
            return null
        }

        // console.log(`[NiftyTrader] Success: ${data.optiondata.length} strikes, ATM: ${data.atm_strike}`)

        return transformNiftyTraderResponse(data)
    } catch (e: any) {
        if (e.name === 'AbortError') {
            console.warn('[NiftyTrader] Request timed out (15s)')
        } else {
            console.error('[NiftyTrader] Fetch error:', e.message)
        }
        return null
    }
}

/**
 * Fetch multiple minutes of data (for catching up missed minutes)
 * Uses rate limiting to avoid overwhelming the API
 */
export async function fetchNiftyTraderBatch(
    symbol: IndexSymbol | string,
    expiry: string,
    times: string[], // Array of times to fetch: ["09:16", "09:16", ...]
    delayMs: number = 200, // Delay between requests
): Promise<Map<string, any>> {
    const results = new Map<string, any>()

    // console.log(`[NiftyTrader] Batch fetching ${times.length} minutes...`)

    for (const time of times) {
        const data = await fetchNiftyTraderMinute(symbol, expiry, time)
        if (data) {
            results.set(time, data)
        }

        // Rate limiting delay
        if (delayMs > 0) {
            await new Promise((resolve) => setTimeout(resolve, delayMs))
        }
    }

    // console.log(`[NiftyTrader] Batch complete: ${results.size}/${times.length} successful`)

    return results
}

// ============================================================================
// FALLBACK WRAPPER
// ============================================================================

/**
 * Fetch live data with NiftyTrader as Tier 1
 * Falls back to Upstox if NiftyTrader fails
 */
export async function fetchLiveWithNiftyTraderFallback(
    symbol: IndexSymbol | string,
    expiry: string,
    upstoxFallback: () => Promise<any>,
): Promise<any> {
    // Tier 1: Try NiftyTrader first
    const niftyTraderData = await fetchNiftyTraderMinute(symbol, expiry)
    if (niftyTraderData) {
        return niftyTraderData
    }

    // console.log('[NiftyTrader] Failed, falling back to Upstox...')

    // Tier 2: Fallback to Upstox
    return upstoxFallback()
}
